import React, { useEffect, useState } from "react";
import { API_URL } from "./api";
import logoLight from "./assets/logo-light.svg";
import logoDark from "./assets/logo-dark.svg";
import "./PortalUI.css";
import "./ReviewPage.css";

function PublicBrand() {
    return (
        <div className="review-brand">
            <picture>
                <source srcSet={logoLight} media="(prefers-color-scheme: dark)" />
                <img src={logoDark} alt="Urban Podcasts" className="review-brand__logo" />
            </picture>
        </div>
    );
}

function getTokenFromUrl() {
    const searchToken = new URLSearchParams(window.location.search).get("token");
    if (searchToken) return searchToken;

    const hash = window.location.hash || "";
    const [, afterHash] = hash.split("#/");
    if (!afterHash) return null;
    const [, qs] = afterHash.split("?");
    if (!qs) return null;

    return new URLSearchParams(qs).get("token");
}

const emptyForm = {
    business_name: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    address: "",
    vat_number: "",
};

function LeadOnboardingPage() {
    const token = getTokenFromUrl();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [form, setForm] = useState(emptyForm);

    useEffect(() => {
        async function load() {
            if (!token) {
                setError("Missing onboarding token in the link.");
                setLoading(false);
                return;
            }

            setLoading(true);
            setError("");
            try {
                const res = await fetch(`${API_URL}/lead-onboarding/${token}`);
                const data = await res.json().catch(() => null);

                if (!res.ok || !data?.ok) {
                    setError(
                        data?.message || "We couldn't load this onboarding form."
                    );
                    return;
                }

                setForm((prev) => ({
                    ...prev,
                    business_name: "",
                    contact_name: data?.lead?.name || "",
                    contact_email: data?.lead?.email || "",
                    contact_phone: data?.lead?.phone || "",
                    address: "",
                    vat_number: "",
                }));
            } catch (err) {
                console.error("Lead onboarding load error:", err);
                setError("We couldn't load this onboarding form.");
            } finally {
                setLoading(false);
            }
        }

        load();
    }, [token]);

    async function handleSubmit(e) {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            const res = await fetch(`${API_URL}/lead-onboarding/${token}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });

            const data = await res.json().catch(() => null);
            if (!res.ok || !data?.ok) {
                setError(
                    data?.message ||
                        "We couldn't submit your onboarding details. Please try again."
                );
                return;
            }

            setSubmitted(true);
        } catch (err) {
            console.error("Lead onboarding submit error:", err);
            setError(
                "We couldn't submit your onboarding details. Please try again."
            );
        } finally {
            setSubmitting(false);
        }
    }

    if (loading) {
        return (
            <div className="review-page">
                <PublicBrand />
                <div className="review-card card review-card--narrow">
                    <p>Loading onboarding form…</p>
                </div>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="review-page">
                <PublicBrand />
                <div className="review-card card review-card--narrow">
                    <h1 className="review-title">Thanks for submitting</h1>
                    <p className="review-subtitle">
                        We have received your onboarding details and will be in touch
                        with next steps.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="review-page">
            <PublicBrand />
            <div className="review-card card">
                <h1 className="review-title">Onboarding</h1>
                <p className="review-subtitle">
                    Thank you for choosing to work with Urban Podcasts - we&apos;re
                    excited to have you on board.
                </p>
                <p className="review-subtitle" style={{ marginTop: 8 }}>
                    To get started, we just need a little information from you. This
                    will allow us to set up your account and ensure everything is ready
                    to go. Once we have your details, we&apos;ll be in touch to guide
                    you through the next steps.
                </p>

                {error && <div className="alert alert--error">{error}</div>}

                <form onSubmit={handleSubmit} style={{ marginTop: 12 }}>
                    <label className="form-field">
                        <span className="form-field__label">
                            Business Name (or Podcast Name if you&apos;re not a business)
                        </span>
                        <input
                            className="form-field__input"
                            value={form.business_name}
                            onChange={(e) =>
                                setForm((prev) => ({
                                    ...prev,
                                    business_name: e.target.value,
                                }))
                            }
                            required
                        />
                    </label>

                    <label className="form-field">
                        <span className="form-field__label">Your Name</span>
                        <input
                            className="form-field__input"
                            value={form.contact_name}
                            onChange={(e) =>
                                setForm((prev) => ({
                                    ...prev,
                                    contact_name: e.target.value,
                                }))
                            }
                            required
                        />
                    </label>

                    <label className="form-field">
                        <span className="form-field__label">Your Email</span>
                        <input
                            type="email"
                            className="form-field__input"
                            value={form.contact_email}
                            onChange={(e) =>
                                setForm((prev) => ({
                                    ...prev,
                                    contact_email: e.target.value,
                                }))
                            }
                            required
                        />
                    </label>

                    <label className="form-field">
                        <span className="form-field__label">Your Phone Number</span>
                        <input
                            className="form-field__input"
                            value={form.contact_phone}
                            onChange={(e) =>
                                setForm((prev) => ({
                                    ...prev,
                                    contact_phone: e.target.value,
                                }))
                            }
                            required
                        />
                    </label>

                    <label className="form-field">
                        <span className="form-field__label">Address</span>
                        <textarea
                            className="form-field__textarea"
                            rows={3}
                            value={form.address}
                            onChange={(e) =>
                                setForm((prev) => ({
                                    ...prev,
                                    address: e.target.value,
                                }))
                            }
                            required
                        />
                    </label>

                    <label className="form-field">
                        <span className="form-field__label">
                            VAT Number (if applicable)
                        </span>
                        <input
                            className="form-field__input"
                            value={form.vat_number}
                            onChange={(e) =>
                                setForm((prev) => ({
                                    ...prev,
                                    vat_number: e.target.value,
                                }))
                            }
                        />
                    </label>

                    <div style={{ marginTop: 16 }}>
                        <button
                            type="submit"
                            className="btn btn--primary"
                            disabled={submitting}
                        >
                            {submitting ? "Submitting..." : "Submit"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default LeadOnboardingPage;
